import { formatDate } from "../../../lib/formatTimestamp";
import type { StatusBadgeColor } from "../../StatusBadge";

const DAY_MS = 1000 * 60 * 60 * 24;

export const SCIM_TOKEN_WARNING_DAYS = 30;

export function daysUntil(timestamp: number, now: number): number {
  return Math.ceil((timestamp - now) / DAY_MS);
}

export type ProviderStatus = {
  label: string;
  color: StatusBadgeColor;
  detail: string | null;
};

export function ssoProviderStatus(
  provider: {
    protocol: string;
    syncMode?: string | null;
    scimTokenExpiresAt?: number | null;
    oidcConfigGeneration?: string | null;
    oidcConfigWrittenGeneration?: string | null;
  },
  now: number
): ProviderStatus {
  if (
    provider.protocol === "oidc" &&
    provider.oidcConfigGeneration != null &&
    provider.oidcConfigWrittenGeneration !== provider.oidcConfigGeneration
  ) {
    return {
      label: "Setting up",
      color: "sky",
      detail:
        "Reading the IdP’s OpenID configuration. If this doesn’t finish, check the issuer URL.",
    };
  }
  if (provider.syncMode === "scim") {
    const expiresAt = provider.scimTokenExpiresAt;
    if (expiresAt == null) {
      return {
        label: "No SCIM token",
        color: "red",
        detail:
          "Your IdP can’t sync without a token. Turn SCIM off and back on to mint one.",
      };
    }
    const days = daysUntil(expiresAt, now);
    if (days <= 0) {
      return {
        label: "Token expired",
        color: "red",
        detail: `The SCIM token expired on ${formatDate(expiresAt)}, so your IdP can’t sync. Revoke it and turn SCIM back on to mint a new one.`,
      };
    }
    if (days <= SCIM_TOKEN_WARNING_DAYS) {
      return {
        label: "Token expiring",
        color: "orange",
        detail: `The SCIM token expires on ${formatDate(expiresAt)}.`,
      };
    }
  }
  return { label: "Ready", color: "green", detail: null };
}
